import React, { useEffect, useState, useRef } from 'react';
import { Formik, Form } from 'formik';
import { CustomMultiSelectDropdown } from 'components/CustomMultiSelectDropdown';
import { CustomSingleSelectDropdown } from 'components/CustomSingleSelectDropdown';
import { CustomTextArea } from 'components/CustomTextArea';
import { EWButton } from 'components/EWButton';
import { getValidationSchema, FORM_FIELDS } from './constant';
import PropTypes from 'prop-types';
import { Caption1, Caption1Stronger } from '@fluentui/react-components';
import { dialog } from '@microsoft/teams-js';
import { getGifList } from 'Services/apiFunctions';
import { GET_GIF_LIST } from 'Services/apiKeys';
import AIEnhanceButton from 'components/AIEnhanceButton';
import { getTeamsChannelName } from 'utils/helperFunctions';
import SelectGifSection from 'components/SelectGifSection';
import { ReactComponent as InfoIcon } from 'assets/images/info-icon.svg';
import './style.scss';

const RecognitionForm = ({
    authToken,
    creditBalance,
    setSubmitLoader,
    messageId,
    selectedGif,
    setSelectedGif,
    isAddOn,
    recognitionData,
}) => {
    const {
        coreValues,
        rewards,
        members,
        channel,
        initialValues,
        isCompanyValueRequired,
        shouldShowRecognitionChannel,
        currentUserId,
        defaultRecognitionChannel,
        departments,
        isAiEnabled,
        allowRecognitionMediaSelection,
        maxValues,
    } = recognitionData;

    const [images, setImages] = useState([]); //GIFs based on the recognition type
    const [recognitionType, setRecognitionType] = useState(initialValues.recognitionType);
    const [deptSummary, setDeptSummary] = useState({ totalDepts: 0, deptIds: [] });
    const [recogChannel, setRecogChannel] = useState(defaultRecognitionChannel);
    const [hasDefaultRecogChannel, setHasDefaultRecogChannel] = useState(false);
    const hasUsersFromOtherDept = useRef(false);
    const [newUsersFromOtherDept, setNewUserFromOtherDept] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    // Fetch GIFs when recognitionType changes
    useEffect(() => {
        if (recognitionType && allowRecognitionMediaSelection) {
            const rewardType = recognitionType?.header?.toLowerCase().includes('shoutout') ? 'shoutout' : 'kudos';
            getGifList({ queryKey: [GET_GIF_LIST, authToken, rewardType] })
                .then((data) => {
                    setImages(data);
                })
                .catch(() => {
                    setImages([]);
                });
        }
    }, [recognitionType]);

    const handleSubmit = (values) => {
        const selectedChannel = (recogChannel?.channelId && recogChannel) || values.recognitionChannel;
        const normalizedChannel = selectedChannel?.channelId
            ? selectedChannel
            : channel?.data?.find((c) => c?.id && c.id === selectedChannel?.id) ||
              channel?.data?.find((c) => c?.channelId && c.channelId === selectedChannel?.channelId) ||
              channel?.data?.find((c) => c?.header && c.header === selectedChannel?.header) ||
              selectedChannel;

        let data = {
            reward: values.recognitionType.id,
            companyValues: values.companyValue.map((value) => value.id),
            reason: values.recognitionReason,
            users: values.recognitionRecipients.map((item) => item.id),
            messageId,
            gifUrl: selectedGif,
            ...(!messageId && { channel: normalizedChannel }),
        };
        setSubmitLoader(true);
        dialog.url.submit({ data: data, startedVia: 'messagingExtension', flagType: 'recognitions_submit' }, [
            process.env.REACT_APP_TEAMS_APP_ID,
            process.env.REACT_APP_TEAMS_APP_ID_NEW,
        ]);
    };

    const checkUsersFromOtherDepartments = ({ values, initialValues }) => {
        const uniqueDeptMap = new Map();
        let noDepartmentUsers = false;

        const initialIds = initialValues.recognitionRecipients.map((recipient) => recipient.id);
        const newlyAddedUsers = values.recognitionRecipients.filter((recipient) => !initialIds.includes(recipient.id));

        if (newlyAddedUsers.length === 0) {
            hasUsersFromOtherDept.current = false;
        } else {
            const filteredUsers = newlyAddedUsers.filter(
                (user) => user.department?.department_id !== initialValues.commonDepartmentId
            );
            hasUsersFromOtherDept.current = filteredUsers.length > 0;
            setNewUserFromOtherDept(filteredUsers);
        }

        values.recognitionRecipients.forEach((user) => {
            const dept = user.department;
            if (!dept) {
                noDepartmentUsers = true;
            } else if (dept.department_id && !uniqueDeptMap.has(dept.department_id)) {
                uniqueDeptMap.set(dept.department_id, dept.name);
            }
        });

        if (uniqueDeptMap.size === 1 && !noDepartmentUsers) {
            const targetDeptId = uniqueDeptMap.keys().next().value;
            const matchedDept = departments?.find((dept) => dept._id === targetDeptId);
            if (matchedDept?.recognitionChannel) {
                setHasDefaultRecogChannel(true);
                setRecogChannel(matchedDept.recognitionChannel);
            } else {
                setHasDefaultRecogChannel(false);
                setRecogChannel(defaultRecognitionChannel);
            }
        } else {
            setHasDefaultRecogChannel(false);
            setRecogChannel(defaultRecognitionChannel);
        }
        setDeptSummary({
            totalDepts: uniqueDeptMap.size,
            deptIds: Array.from(uniqueDeptMap.keys()),
            noDepartmentUsers,
        });
        setSelectedUsers(values.recognitionRecipients);
    };
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={getValidationSchema(
                isCompanyValueRequired,
                messageId,
                currentUserId,
                creditBalance,
                maxValues
            )}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ values, setFieldValue }) => {
                useEffect(() => {
                    checkUsersFromOtherDepartments({ values, initialValues });
                }, [values.recognitionRecipients]);
                return (
                    <Form>
                        <CustomSingleSelectDropdown
                            {...FORM_FIELDS.recognitionType}
                            isDropdownOptionLoading={rewards.isFetching}
                            dropdownOption={rewards.data}
                            onChange={(value) => setRecognitionType(value)}
                        />

                        <CustomMultiSelectDropdown
                            {...FORM_FIELDS.recognitionRecipients}
                            isDropdownOptionLoading={members.isFetching}
                            dropdownOption={members.data}
                        />
                        <CustomTextArea
                            {...FORM_FIELDS.recognitionReason}
                            label={
                                <div className='enhance_ai_btn'>
                                    <span>
                                        Why do you want to recognize?{' '}
                                        {messageId && <span className='optional-label'>(Optional)</span>}
                                    </span>

                                    {isAiEnabled && (
                                        <AIEnhanceButton
                                            reason={values.recognitionReason}
                                            setReason={(reason) => setFieldValue('recognitionReason', reason)}
                                            authToken={authToken}
                                        />
                                    )}
                                </div>
                            }
                        />
                        <CustomMultiSelectDropdown
                            {...FORM_FIELDS.companyValue}
                            label={
                                <>
                                    Company Value{' '}
                                    {isCompanyValueRequired ? '' : <span className='optional-label'>(Optional)</span>}
                                </>
                            }
                            isDropdownOptionLoading={coreValues.isFetching}
                            dropdownOption={coreValues.data}
                            render={coreValues.data.length > 0}
                        />
                        {!recogChannel?.channelId && (
                            <CustomSingleSelectDropdown
                                {...FORM_FIELDS.recognitionChannel}
                                isDropdownOptionLoading={channel.isFetching}
                                dropdownOption={channel.data}
                                render={shouldShowRecognitionChannel}
                            />
                        )}

                        {!isAddOn && recogChannel?.channelId && selectedUsers.length > 0 ? (
                            <div className='body-2'>
                                <Caption1Stronger>Note:</Caption1Stronger>
                                <Caption1> Recognition will be sent out in </Caption1>
                                <Caption1Stronger>{getTeamsChannelName(recogChannel)}</Caption1Stronger>
                            </div>
                        ) : null}

                        {!isAddOn &&
                            selectedUsers.length > 1 &&
                            deptSummary.totalDepts === 1 &&
                            !deptSummary.noDepartmentUsers &&
                            hasDefaultRecogChannel && (
                                <div className='info-container'>
                                    <InfoIcon className='info-icon' />
                                    <p>
                                        Recipients are from the same department. This recognition will be posted in the
                                        department&apos;s default channel.
                                    </p>
                                </div>
                            )}
                        {!isAddOn &&
                            recogChannel?.channelId &&
                            (deptSummary.totalDepts > 1 ||
                                (deptSummary.totalDepts === 1 && deptSummary.noDepartmentUsers)) && (
                                <div className='info-container'>
                                    <InfoIcon className='info-icon' />
                                    <p>
                                        Recipients are from different or unassigned departments, this recognition will
                                        be posted in the org-wide channel.
                                    </p>
                                </div>
                            )}
                        {isAddOn && hasUsersFromOtherDept.current && (
                            <div className='info-container'>
                                <InfoIcon className='info-icon' />
                                <p>
                                    <span className='name-tag'>{`@${newUsersFromOtherDept[0].header}`}</span>{' '}
                                    {newUsersFromOtherDept.length > 1 && (
                                        <>
                                            and <strong>{newUsersFromOtherDept.length - 1} more</strong>{' '}
                                        </>
                                    )}
                                    may not see this recognition as they are part of different department. Consider
                                    sending a new recognition instead.
                                </p>
                            </div>
                        )}
                        {!isAddOn && allowRecognitionMediaSelection && (
                            <SelectGifSection
                                images={images}
                                selectedImage={selectedGif}
                                setSelectedImage={setSelectedGif}
                            />
                        )}

                        <EWButton appearance='primary' buttonText='Submit' />
                    </Form>
                );
            }}
        </Formik>
    );
};

RecognitionForm.propTypes = {
    authToken: PropTypes.string,
    setSubmitLoader: PropTypes.func,
    messageId: PropTypes.string,
    creditBalance: PropTypes.number,
    selectedGif: PropTypes.string.isRequired,
    setSelectedGif: PropTypes.func.isRequired,
    isAddOn: PropTypes.bool.isRequired,
    recognitionData: PropTypes.object.isRequired,
};

export default RecognitionForm;
